// Panels des Objekts (README §7) in der Reihenfolge der Sidebar (Abschnitt „OBJEKT“, Prototyp).
// Die Objektseite (E3) nutzt dieselben Schlüssel für ?panel=<key>.

import {
  AddressBook, ArrowCircleDown, Bank, CalendarBlank, ChartDonut, ChartPieSlice, ClockCounterClockwise, CrownSimple,
  CalendarCheck, CurrencyEur, EnvelopeSimpleOpen, FolderSimple, Gauge, GearSix, Key, MapPinArea, MapTrifold, NotePencil, PiggyBank, Receipt,
  Ruler, ShieldCheck, Signature, Sparkle, Ticket, TrendUp, Umbrella, UserCircleCheck, UsersThree, Wrench,
  type Icon,
} from '@phosphor-icons/react';
import type { VerwaltungsartKurz } from '../../lib/immobilien/sidebarNav';

export type ObjektPanelKey =
  | 'karte' | 'flaeche' | 'beleg' | 'kalt' | 'wartung' | 'protokoll' | 'keys' | 'bank' | 'grund' | 'wplan' | 'etv'
  | 'mieterh' | 'mahn' | 'versich' | 'vertraege' | 'tickets' | 'mieten' | 'ehem' | 'eigent' | 'weg' | 'zaehler'
  | 'termine' | 'kontakte' | 'notizen' | 'dienstleister' | 'dokumente' | 'aliase' | 'zust' | 'stamm'
  | 'finanzen' | 'bericht';

export interface ObjektPanelDef {
  key: ObjektPanelKey;
  /** Beschriftung in der Sidebar */
  label: string;
  icon: Icon;
  /** Nur für bestimmte Verwaltungsarten anbieten */
  nur?: 'weg' | 'nichtWeg';
}

export const OBJEKT_PANELS: readonly ObjektPanelDef[] = [
  { key: 'karte', label: 'Lage & Karte', icon: MapTrifold },
  { key: 'flaeche', label: 'Flächen', icon: Ruler },
  { key: 'beleg', label: 'Belegung', icon: ChartDonut },
  { key: 'kalt', label: 'Kaltmiete', icon: CurrencyEur },
  { key: 'wartung', label: 'Wartung & Prüfungen', icon: ShieldCheck },
  { key: 'protokoll', label: 'Aktivitätsprotokoll', icon: ClockCounterClockwise },
  { key: 'keys', label: 'Schlüssel', icon: Key },
  { key: 'bank', label: 'Bankkonten', icon: Bank },
  { key: 'grund', label: 'Grundbuch & Grundsteuer', icon: MapPinArea },
  { key: 'wplan', label: 'Wirtschaftsplan & Rücklage', icon: PiggyBank, nur: 'weg' },
  { key: 'etv', label: 'ETV & Beschlüsse', icon: UsersThree, nur: 'weg' },
  { key: 'mieterh', label: 'Mieterhöhung', icon: TrendUp, nur: 'nichtWeg' },
  { key: 'mahn', label: 'Mahnwesen', icon: EnvelopeSimpleOpen },
  { key: 'versich', label: 'Versicherungen', icon: Umbrella },
  { key: 'vertraege', label: 'Verträge', icon: Signature },
  { key: 'tickets', label: 'Tickets', icon: Ticket },
  { key: 'mieten', label: 'Mieteingänge', icon: ArrowCircleDown },
  { key: 'ehem', label: 'Ehemalige Mieter', icon: ClockCounterClockwise },
  { key: 'eigent', label: 'Eigentümer', icon: CrownSimple },
  { key: 'weg', label: 'MEA & Hausgeld', icon: ChartPieSlice, nur: 'weg' },
  { key: 'zaehler', label: 'Zähler', icon: Gauge },
  { key: 'termine', label: 'Termine', icon: CalendarBlank },
  { key: 'kontakte', label: 'Kontakte', icon: AddressBook },
  { key: 'notizen', label: 'Notizen', icon: NotePencil },
  { key: 'dienstleister', label: 'Dienstleister', icon: Wrench },
  { key: 'dokumente', label: 'Dokumente', icon: FolderSimple },
  { key: 'aliase', label: 'KI-Aliase', icon: Sparkle },
  { key: 'zust', label: 'Zuständig', icon: UserCircleCheck },
  { key: 'stamm', label: 'Technik & Stammdaten', icon: GearSix },
];

/** Panels, die für die Verwaltungsart angeboten werden. Ohne Verwaltungsart: wie Mietverwaltung. */
export function panelsFuer(va: VerwaltungsartKurz | null): ObjektPanelDef[] {
  const weg = va === 'WEG';
  return OBJEKT_PANELS.filter(p => !p.nur || (p.nur === 'weg' ? weg : !weg));
}

/**
 * Panels, die auf der neuen Objektseite schon funktionieren. Alle anderen erscheinen in
 * Sidebar und Raster, sind aber als „noch nicht angebunden“ markiert.
 */
export const ANGEBUNDENE_PANELS: ReadonlySet<ObjektPanelKey> = new Set<ObjektPanelKey>([
  // E3a
  'stamm', 'karte', 'flaeche', 'beleg', 'kalt', 'ehem', 'eigent', 'zust', 'aliase', 'notizen',
  // E3b
  'termine', 'zaehler', 'dokumente', 'kontakte', 'dienstleister',
  // E8
  'tickets', 'wartung', 'versich', 'vertraege', 'etv', 'keys', 'protokoll', 'mieterh', 'weg', 'wplan', 'bericht',
  // Steuerpaket S1
  'finanzen',
]);

/** Kopf des Morph-Panels: Titel · Icon (README §7). */
export const PANEL_KOPF: Record<ObjektPanelKey, { titel: string; icon: Icon }> = {
  beleg: { titel: 'Belegung & Leerstand', icon: ChartDonut },
  flaeche: { titel: 'Flächen', icon: Ruler },
  karte: { titel: 'Lage & Karte', icon: MapTrifold },
  stamm: { titel: 'Technik & Stammdaten', icon: GearSix },
  mieten: { titel: 'Mieteingang', icon: CalendarCheck },
  kalt: { titel: 'Kaltmiete & Mietspiegel', icon: CurrencyEur },
  mahn: { titel: 'Mahnwesen', icon: EnvelopeSimpleOpen },
  mieterh: { titel: 'Mieterhöhung', icon: TrendUp },
  ehem: { titel: 'Ehemalige Mieter', icon: ClockCounterClockwise },
  kontakte: { titel: 'Kontakte', icon: AddressBook },
  keys: { titel: 'Schlüsselverwaltung', icon: Key },
  finanzen: { titel: 'Finanzen', icon: Receipt },
  bank: { titel: 'Bankkonten & Abgleich', icon: Bank },
  weg: { titel: 'WEG · MEA & Hausgeld', icon: ChartPieSlice },
  wplan: { titel: 'Wirtschaftsplan & Rücklage', icon: PiggyBank },
  grund: { titel: 'Grundbuch & Grundsteuer', icon: MapPinArea },
  tickets: { titel: 'Tickets', icon: Ticket },
  wartung: { titel: 'Wartung & Prüfpflichten', icon: ShieldCheck },
  zaehler: { titel: 'Zähler', icon: Gauge },
  dienstleister: { titel: 'Dienstleister', icon: Wrench },
  vertraege: { titel: 'Verträge', icon: Signature },
  versich: { titel: 'Versicherungen', icon: Umbrella },
  eigent: { titel: 'Eigentümer', icon: CrownSimple },
  zust: { titel: 'Zuständigkeit', icon: UserCircleCheck },
  etv: { titel: 'Eigentümerversammlung & Beschlüsse', icon: UsersThree },
  termine: { titel: 'Termine', icon: CalendarBlank },
  dokumente: { titel: 'Dokumente', icon: FolderSimple },
  notizen: { titel: 'Notizen', icon: NotePencil },
  protokoll: { titel: 'Aktivitätsprotokoll', icon: ClockCounterClockwise },
  aliase: { titel: 'KI-Aliase', icon: Sparkle },
  bericht: { titel: 'Berichtscenter', icon: ChartPieSlice },
};

export function istPanelKey(wert: string | null): wert is ObjektPanelKey {
  return wert !== null && Object.prototype.hasOwnProperty.call(PANEL_KOPF, wert);
}
